import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneRight, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { EMAILS } from "@/lib/emails";

const WHATSAPP_CHAT_URL = "https://api.whatsapp.com/message/E3HBB5VC6S7VL1?autoload=1&app_absent=0";

export default function Contact() {
  useSeo("Contact", "Get in touch with the AlumniPulse team.");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Name, email and message are required");
    setBusy(true);
    try { await api.post("/contact", form); setSent(true); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-14">
      <div className="grid gap-10 lg:grid-cols-2">
        <div>
          <p className="overline text-secondary mb-3">We'd love to hear from you</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Get in touch</h1>
          <p className="mt-4 text-muted-foreground">Questions, partnerships, or want to bring your institution onboard? Send us a note and we'll get back to you.</p>
          <a href={`mailto:${EMAILS.contact}`} className="mt-6 inline-flex items-center gap-2 rounded-full border border-border/60 px-4 py-2 text-sm font-medium hover:border-secondary/50">
            <EnvelopeSimple size={18} className="text-secondary" /> {EMAILS.contact}
          </a>
          <div className="mt-3 space-y-1 text-sm text-muted-foreground">
            <p>General info: <a href={`mailto:${EMAILS.info}`} className="text-secondary hover:underline">{EMAILS.info}</a></p>
            <p>Privacy & data: <a href={`mailto:${EMAILS.privacy}`} className="text-secondary hover:underline">{EMAILS.privacy}</a></p>
            <p>Report abuse: <a href={`mailto:${EMAILS.abuse}`} className="text-secondary hover:underline">{EMAILS.abuse}</a></p>
          </div>

          <div className="mt-6 rounded-2xl border border-[#25D366]/40 bg-[#25D366]/10 p-5" data-testid="whatsapp-contact-card">
            <div className="flex items-center gap-2 text-[#128C7E]">
              <WhatsappLogo size={22} weight="fill" />
              <h2 className="font-heading text-lg font-medium">Chat with us on WhatsApp</h2>
            </div>
            <p className="mt-1 text-sm text-[#075E54]/70">Prefer WhatsApp? Message us directly and we'll reply as soon as we can.</p>
            <a href={WHATSAPP_CHAT_URL} target="_blank" rel="noreferrer" data-testid="whatsapp-chat-button"
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1ebe5a]">
              <WhatsappLogo size={18} weight="fill" /> Chat on WhatsApp
            </a>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-7 shadow-sm">
          {sent ? (
            <div className="grid place-items-center py-12 text-center" data-testid="contact-success">
              <CheckCircle size={44} weight="duotone" className="text-secondary" />
              <h3 className="mt-3 font-heading text-xl">Message sent!</h3>
              <p className="mt-1 text-sm text-muted-foreground">Thanks, {form.name.split(" ")[0]}. We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
                <div><Label>Email</Label><Input type="email" className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" /></div>
              </div>
              <div><Label>Subject</Label><Input className="mt-1.5" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" /></div>
              <div><Label>Message</Label><Textarea rows={5} className="mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" /></div>
              <Button type="submit" disabled={busy} className="w-full rounded-full gap-1.5" data-testid="contact-submit"><PaperPlaneRight size={16} weight="fill" /> {busy ? "Sending…" : "Send message"}</Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
